import React, { useState, useEffect } from "react";
import { Paper, Box, CircularProgress, Theme } from "@material-ui/core";
import { styled, useTheme } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import ProgressBar from "../progress-bar";
import StepInitial from "./StepInitial";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { injectIntl } from "react-intl";
import Content from "../content";
import Button from "../button";
import Link from "../link";
import Typography from "../typography";
import SvgIcon from "@material-ui/core/SvgIcon";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// Custom Cone Icon for soccer drills
const ConeIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 2L4 22h16L12 2zm0 4.5l5.5 13.5h-11L12 6.5z" />
    <path d="M8 14h8v1.5H8zM7 17h10v1.5H7z" opacity="0.6" />
  </SvgIcon>
);

export const ADD_EXERCISE = gql`
  mutation AddExercise(
    $name: String!
    $shortDescription: String
    $category: String!
    $subcategory: String
    $objectives: [String]
    $taskDevelopment: String
  ) {
    addExercise(
      input: {
        name: $name
        shortDescription: $shortDescription
        category: $category
        subcategory: $subcategory
        objectives: $objectives
        taskDevelopment: $taskDevelopment
      }
    ) {
      id
      name
      shortDescription
      category
      subcategory
      objectives
      taskDevelopment
    }
  }
`;

const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  minHeight: "100vh",
  background: theme.palette.extra.backgroundGrey,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(10),
  },
}));

const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: "border-box",
  padding: theme.spacing(5, 6),
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  maxWidth: 900,
  minHeight: "70vh",
  borderRadius: "18px",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
    borderRadius: "16px",
    padding: theme.spacing(3, 2.5),
    margin: theme.spacing(0, 2),
    minHeight: "auto",
  },
}));

// Back link
const BackLink = styled(Link)(({ theme }: IStyledArguments) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  color: theme.palette.primary.dark,
  fontSize: "0.9rem",
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  textDecoration: "none",
  "& svg": {
    fontSize: "1.2rem",
  },
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const HeaderSection = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: theme.spacing(4),
  paddingTop: theme.spacing(2),
}));

const HeaderIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 88,
  height: 88,
  borderRadius: "24px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2.5),
  "& svg": {
    fontSize: "2.8rem",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    width: 72,
    height: 72,
    borderRadius: "20px",
    "& svg": {
      fontSize: "2.2rem",
    },
  },
}));

const HeaderTitle = styled(Typography.H4)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "2rem",
  letterSpacing: "-0.5px",
  textAlign: "center",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
  },
}));

const HeaderSubtitle = styled("p")(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "1rem",
  textAlign: "center",
  margin: 0,
  marginTop: theme.spacing(1),
  maxWidth: 400,
  lineHeight: 1.5,
}));

const StyledProgressBar = styled("div")(({ theme }: IStyledArguments) => ({
  maxWidth: 500,
  margin: "0 auto",
  marginBottom: theme.spacing(4),
  "& .MuiLinearProgress-root": {
    borderRadius: 10,
    height: 10,
    backgroundColor: theme.palette.extra.accentSoft,
  },
  "& .MuiLinearProgress-bar": {
    borderRadius: 10,
    background: theme.palette.primary.main,
  },
}));

// Form Container - more spacious
const FormContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
}));

// Success State
const SuccessContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  flex: 1,
  justifyContent: "center",
}));

const SuccessIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 120,
  height: 120,
  borderRadius: "50%",
  background: theme.palette.extra.success,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(4),
  "& svg": {
    fontSize: "4rem",
    color: "#fff",
  },
}));

const SuccessTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  fontSize: "1.8rem",
  color: theme.palette.extra.success,
  marginBottom: theme.spacing(1.5),
}));

const SuccessMessage = styled("p")(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(4),
  maxWidth: 350,
  lineHeight: 1.6,
}));

const SuccessButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.success,
  color: "#fff",
  fontWeight: 700,
  borderRadius: "16px",
  padding: theme.spacing(1.75, 6),
  textTransform: "none",
  fontSize: "1.1rem",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.9,
  },
}));

const StyledActionLink = styled(Link)(() => ({
  textDecoration: "none",
}));

// Loading State
const LoadingContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
  gap: theme.spacing(3),
}));

const LoadingText = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "1.1rem",
}));

const PageNewExercise = () => {
  const theme = useTheme<Theme>();
  const [exerciseDetails, setExerciseDetails] = useState();
  const [currentStep, setCurrentStep] = useState(1);

  const [addExercise, { data, loading }] = useMutation(ADD_EXERCISE);

  const handleAddExercise = async (form: any) => {
    addExercise({
      variables: {
        name: form.name,
        shortDescription: form.shortDescription,
        category: form.category,
        subcategory: form.subcategory,
        objectives: form.objectives,
        taskDevelopment: form.taskDevelopment,
      },
    });
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!data) {
      return;
    } else {
      setExerciseDetails(data.addExercise);
      setCurrentStep(2);
    }
  }, [data]);

  if (loading) {
    return (
      <PageContainer>
        <Content>
          <StyledPaper elevation={0}>
            <LoadingContainer>
              <CircularProgress style={{ color: theme.palette.primary.main }} size={56} thickness={4} />
              <LoadingText>Creating your drill...</LoadingText>
            </LoadingContainer>
          </StyledPaper>
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Content>
        <StyledPaper elevation={0}>
          <BackLink to="/library/drills">
            <ArrowBackIcon />
            Back to Library
          </BackLink>
          
          <HeaderSection>
            <HeaderIcon>
              <ConeIcon />
            </HeaderIcon>
            <HeaderTitle>Create New Drill</HeaderTitle>
            <HeaderSubtitle>
              Add a new training drill to your library. Fill in the details below.
            </HeaderSubtitle>
          </HeaderSection>
          
          <StyledProgressBar>
            <ProgressBar steps={2} activeStep={currentStep} />
          </StyledProgressBar>
          
          <FormContainer>
            {currentStep === 1 && (
              <StepInitial
                exerciseDetails={exerciseDetails}
                onGoNext={(values: any) => {
                  handleAddExercise(values);
                }}
              />
            )}

            {currentStep === 2 && (
              <SuccessContainer>
                <SuccessIcon>
                  <CheckCircleIcon />
                </SuccessIcon>
                <SuccessTitle>Drill Created!</SuccessTitle>
                <SuccessMessage>
                  Your new drill has been added to the library successfully and is ready to use in your training sessions.
                </SuccessMessage>
                <StyledActionLink to="/library/drills">
                  <SuccessButton>Back to Library</SuccessButton>
                </StyledActionLink>
              </SuccessContainer>
            )}
          </FormContainer>
        </StyledPaper>
      </Content>
    </PageContainer>
  );
};

export default injectIntl(PageNewExercise);
